import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Slider,
  Text,
  Table,
  Button,
  Alert,
  Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceFloppy, IconInfoCircle } from '@tabler/icons-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { getConfigByPrefix, updateConfig } from '../api/prediction-api';

// Power curve formula
function powerScore(
  remainingSupply: number,
  scale: number,
  exponent: number,
  weight: number
): number {
  const normalized = remainingSupply / scale;
  return Math.sign(normalized) * Math.pow(Math.abs(normalized), exponent) * scale * weight;
}

// Linear score for comparison
function linearScore(remainingSupply: number, weight: number): number {
  return remainingSupply * weight;
}

export default function SupplyCurveConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Slider values
  const [exponent, setExponent] = useState(1.5);
  const [scale, setScale] = useState(5);
  const [weight, setWeight] = useState(1.0);

  // Load current config
  useEffect(() => {
    async function loadConfig() {
      try {
        setLoading(true);
        const config = await getConfigByPrefix('scheduling.solver');
        
        // SAFETY: Apply CODING_STANDARDS.md - validate config exists
        if (config) {
          if (config['scheduling.solverSupplyExponent']?.value) {
            setExponent(config['scheduling.solverSupplyExponent'].value);
          }
          if (config['scheduling.solverSupplyScale']?.value) {
            setScale(config['scheduling.solverSupplyScale'].value);
          }
          if (config['scheduling.solverWeightSupply']?.value) {
            setWeight(config['scheduling.solverWeightSupply'].value);
          }
        }
      } catch (error) {
        console.error('Error loading config:', error);
        notifications.show({
          title: 'Error',
          message: 'Failed to load configuration',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, []);

  // Generate chart data
  const chartData = [];
  for (let supply = -15; supply <= 15; supply += 0.5) {
    chartData.push({
      supply,
      linear: linearScore(supply, weight),
      power: powerScore(supply, scale, exponent, weight),
    });
  }

  // Table data points
  const tablePoints = [
    { supply: 10, label: '+10h (needs lots)' },
    { supply: 5, label: '+5h' },
    { supply: 2, label: '+2h' },
    { supply: 0, label: '0 (at target)' },
    { supply: -2, label: '-2h (slightly over)' },
    { supply: -5, label: '-5h (over by 5)' },
    { supply: -10, label: '-10h (way over)' },
  ];

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save all three values
      await updateConfig('scheduling.solverSupplyExponent', exponent);
      await updateConfig('scheduling.solverSupplyScale', scale);
      await updateConfig('scheduling.solverWeightSupply', weight);

      notifications.show({
        title: 'Success',
        message: 'Configuration saved! Changes will take effect on next scheduling run.',
        color: 'green',
      });
    } catch (error) {
      console.error('Error saving config:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save configuration',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Group gap="xs">
          <Loader size="sm" />
          <Text>Loading configuration...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Title order={1}>Supply Curve Configuration</Title>

        <Paper shadow="sm" p="xl" withBorder>
          <Stack gap="xl">
            {/* Sliders */}
            <Stack gap="lg">
              <div>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>Exponent</Text>
                  <Text fw={600} c="blue">{exponent.toFixed(1)}</Text>
                </Group>
                <Slider
                  value={exponent}
                  onChange={setExponent}
                  min={1.0}
                  max={3.0}
                  step={0.1}
                  marks={[
                    { value: 1.0, label: '1.0' },
                    { value: 1.5, label: '1.5' },
                    { value: 2.0, label: '2.0' },
                    { value: 2.5, label: '2.5' },
                    { value: 3.0, label: '3.0' },
                  ]}
                />
              </div>

              <div>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>Scale (hours)</Text>
                  <Text fw={600} c="blue">{scale.toFixed(1)}h</Text>
                </Group>
                <Slider
                  value={scale}
                  onChange={setScale}
                  min={1}
                  max={10}
                  step={0.5}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 3, label: '3' },
                    { value: 5, label: '5' },
                    { value: 7, label: '7' },
                    { value: 10, label: '10' },
                  ]}
                />
              </div>

              <div>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>Weight</Text>
                  <Text fw={600} c="blue">{weight.toFixed(1)}</Text>
                </Group>
                <Slider
                  value={weight}
                  onChange={setWeight}
                  min={0.1}
                  max={3.0}
                  step={0.1}
                  marks={[
                    { value: 0.1, label: '0.1' },
                    { value: 1.0, label: '1.0' },
                    { value: 2.0, label: '2.0' },
                    { value: 3.0, label: '3.0' },
                  ]}
                />
              </div>
            </Stack>

            {/* Interactive Graph */}
            <div>
              <Title order={4} mb="md">Curve Visualization</Title>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="supply"
                    label={{ value: 'Remaining Supply (hours)', position: 'insideBottom', offset: -5 }}
                    domain={[-15, 15]}
                  />
                  <YAxis
                    label={{ value: 'Score Contribution', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip />
                  <Legend />
                  <ReferenceLine x={0} stroke="#666" strokeDasharray="3 3" />
                  <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                  <Line
                    type="monotone"
                    dataKey="linear"
                    stroke="#2196f3"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="Linear (reference)"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="power"
                    stroke="#f44336"
                    strokeWidth={3}
                    name="Power (current)"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Values Table */}
            <div>
              <Title order={4} mb="md">Score Values at Key Points</Title>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Remaining Supply</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Linear</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Power</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tablePoints.map((point) => {
                    const linearVal = linearScore(point.supply, weight);
                    const powerVal = powerScore(point.supply, scale, exponent, weight);
                    
                    return (
                      <Table.Tr key={point.supply}>
                        <Table.Td>{point.label}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={500}>{linearVal.toFixed(1)}</Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text fw={500} c={Math.abs(powerVal) > Math.abs(linearVal) ? 'red' : 'gray'}>
                            {powerVal.toFixed(1)}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </div>

            {/* Save Button */}
            <Group justify="flex-end">
              <Button
                leftSection={<IconDeviceFloppy size={20} />}
                onClick={handleSave}
                loading={saving}
                size="lg"
              >
                Save Configuration
              </Button>
            </Group>

            {/* Help Text */}
            <Alert icon={<IconInfoCircle size={20} />} title="Understanding the Curve" color="blue">
              <Stack gap="xs">
                <div>
                  <Text fw={600} size="sm">Exponent:</Text>
                  <Text size="sm" c="dimmed">
                    • 1.0 = Linear (current behavior)
                  </Text>
                  <Text size="sm" c="dimmed">
                    • 1.5 = Moderate curve (recommended)
                  </Text>
                  <Text size="sm" c="dimmed">
                    • 2.0+ = Aggressive curve (strong correction for large deviations)
                  </Text>
                </div>

                <div>
                  <Text fw={600} size="sm">Scale:</Text>
                  <Text size="sm" c="dimmed">
                    The number of hours where Power curve crosses Linear. At ±{scale.toFixed(1)}h (current), both curves give the same score.
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Below scale: Power is flatter (tolerates small deviations)
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Above scale: Power is steeper (penalizes large deviations more)
                  </Text>
                </div>

                <div>
                  <Text fw={600} size="sm">What this means:</Text>
                  <Text size="sm" c="dimmed">
                    • Employees close to target: small score changes (won't fight over shifts)
                  </Text>
                  <Text size="sm" c="dimmed">
                    • Employees far from target: large score changes (will strongly prioritize/deprioritize)
                  </Text>
                </div>

                <Text size="sm" fw={500} c="orange">
                  ⚠️ Changes take effect on next scheduling run
                </Text>
              </Stack>
            </Alert>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
